import React, { useEffect, useState } from 'react';
import { deleteUser, fetchUserAttributes, updateUserAttributes } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { formatPhone, toE164 } from '../phone';
import Hint from './Hint';

/**
 * Lets a signed-in attendee correct their own registration answers.
 *
 * All six custom attributes are Mutable in the user pool and writable by the app
 * client, so this needs no admin API — Amplify updates them with the user's own
 * token. Email is deliberately not editable: it's the sign-in identifier, and
 * changing it would unverify the account.
 */
const OFFER_TENT_HELP =
  "Say Yes if you have room in your tent for someone who needs a spot. Other approved attendees will see a badge on the Who's Coming page so they know who to ask.";

const OFFER_RIDE_HELP =
  "Say Yes if you have room in your vehicle and could pick someone up on the way. Other approved attendees will see a badge on the Who's Coming page so they know who to ask.";

const SHOW_PHONE_HELP =
  "Yes means other approved attendees can see your phone number on the Who's Coming page, so people can coordinate rides and tents. Organizers can always see it either way.";

export default function MyRegistration(): JSX.Element {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [additional, setAdditional] = useState('0');
  const [bringing, setBringing] = useState('');
  const [tentShare, setTentShare] = useState('No');
  const [needsRide, setNeedsRide] = useState('No');
  const [hasDietary, setHasDietary] = useState('No');
  const [dietaryNotes, setDietaryNotes] = useState('');
  const [showPhone, setShowPhone] = useState('No');
  const [offersTent, setOffersTent] = useState('No');
  const [offersRide, setOffersRide] = useState('No');
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const a = await fetchUserAttributes();
        if (cancelled) return;

        setFullName(a['custom:full.name'] ?? '');
        setPhone(formatPhone(a.phone_number ?? ''));
        setAdditional(a['custom:additional.attend.ex'] ?? a['custom:additional.attendies'] ?? '0');
        setBringing(a['custom:bringing'] ?? '');
        setTentShare(a['custom:share.tent'] ?? '');
        setNeedsRide(a['custom:need.help.travel'] ?? '');
        // Absent means opted out - never publish a number by default.
        setShowPhone(a['custom:show.phone.number'] === 'Yes' ? 'Yes' : 'No');
        setOffersTent(a['custom:open.to.sharing'] === 'Yes' ? 'Yes' : 'No');
        setOffersRide(a['custom:open.to.giving.ride'] === 'Yes' ? 'Yes' : 'No');

        // Signup stores either the specifics or the literal "None".
        const diet = a['custom:dietary.restrictions'] ?? '';
        if (diet && diet !== 'None') {
          setHasDietary('Yes');
          setDietaryNotes(diet);
        } else if (diet === 'None') {
          setHasDietary('No');
        }
      } catch {
        if (!cancelled) setError('Could not load your registration.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSaved(false);

    if (!fullName.trim()) {
      setError('Enter your full name.');
      return;
    }

    const phoneNumber = toE164(phone);
    if (!phoneNumber) {
      setError('Enter a valid phone number, for example (423) 555-0123.');
      return;
    }

    if (!tentShare || !needsRide || !hasDietary) {
      setError('Please answer all of the Yes/No questions.');
      return;
    }

    if (hasDietary === 'Yes' && !dietaryNotes.trim()) {
      setError('Please list your dietary restrictions or allergies.');
      return;
    }

    setSaving(true);
    try {
      await updateUserAttributes({
        userAttributes: {
          'custom:full.name': fullName.trim(),
          phone_number: phoneNumber,
          'custom:additional.attend.ex': additional,
          'custom:share.tent': tentShare,
          'custom:show.phone.number': showPhone,
          'custom:open.to.sharing': offersTent,
          'custom:open.to.giving.ride': offersRide,
          'custom:need.help.travel': needsRide,
          'custom:dietary.restrictions':
            hasDietary === 'Yes' ? dietaryNotes.trim() : 'None',
          // Cognito rejects empty values, so blank means "a single space" is wrong —
          // send a dash to mean "nothing listed" instead.
          'custom:bringing': bringing.trim() || '-',
        },
      });
      setSaved(true);
    } catch (err) {
      setError((err as { message?: string })?.message || 'Could not save your changes.');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove() {
    setError('');
    setRemoving(true);
    try {
      await deleteUser();
      // The session is dead once the account is gone; clearing local state can
      // fail harmlessly, so don't let it block the redirect.
      await logout().catch(() => {});
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        (err as { message?: string })?.message || 'Could not remove your registration.'
      );
      setRemoving(false);
    }
  }

  if (loading) {
    return (
      <div className="card">
        <h2>📝 My Registration</h2>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="card auth-card">
      <h2>📝 My Registration</h2>
      <p>Update your answers any time — this is what the organizers plan around.</p>

      <form onSubmit={handleSubmit}>
        <label>
          Full Name
          <input value={fullName} onChange={e => setFullName(e.target.value)} />
        </label>

        <label>
          Phone Number
          <input
            type="tel"
            autoComplete="tel"
            placeholder="(423) 555-0123"
            value={phone}
            onChange={e => setPhone(e.target.value)}
          />
        </label>

        <label>
          <span className="label-row">
            Show my phone number to other attendees?
            <Hint label={SHOW_PHONE_HELP}>{SHOW_PHONE_HELP}</Hint>
          </span>
          <select value={showPhone} onChange={e => setShowPhone(e.target.value)}>
            <option value="No">No — keep it private</option>
            <option value="Yes">Yes — show it on Who's Coming</option>
          </select>
        </label>

        <label>
          How many additional people are you bringing? (Children)
          <select value={additional} onChange={e => setAdditional(e.target.value)}>
            <option value="0">0</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
          </select>
        </label>

        <label>
          What are you bringing for the group? (Games or food — optional)
          <input value={bringing} onChange={e => setBringing(e.target.value)} />
        </label>

        <label>
          Do you need to share a tent with someone?
          <select value={tentShare} onChange={e => setTentShare(e.target.value)}>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </label>

        <label>
          Do you need help getting to the campsite?
          <select value={needsRide} onChange={e => setNeedsRide(e.target.value)}>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </label>

        <label>
          <span className="label-row">
            Do you have room in your tent for someone?
            <Hint label={OFFER_TENT_HELP}>{OFFER_TENT_HELP}</Hint>
          </span>
          <select value={offersTent} onChange={e => setOffersTent(e.target.value)}>
            <option value="No">No</option>
            <option value="Yes">Yes — I can share my tent</option>
          </select>
        </label>

        <label>
          <span className="label-row">
            Could you give someone a ride?
            <Hint label={OFFER_RIDE_HELP}>{OFFER_RIDE_HELP}</Hint>
          </span>
          <select value={offersRide} onChange={e => setOffersRide(e.target.value)}>
            <option value="No">No</option>
            <option value="Yes">Yes — I can give a ride</option>
          </select>
        </label>

        <label>
          Any dietary restrictions or food allergies?
          <select value={hasDietary} onChange={e => setHasDietary(e.target.value)}>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </label>

        {hasDietary === 'Yes' && (
          <label>
            Please specify (e.g. Vegan, Gluten-Free, Nut Allergy)
            <input value={dietaryNotes} onChange={e => setDietaryNotes(e.target.value)} />
          </label>
        )}

        {error && <p className="error">{error}</p>}
        {saved && <p className="success">Saved.</p>}

        <div className="form-actions">
          <button type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>

      <div className="danger-zone">
        <h3>Not able to make it?</h3>
        <p>
          Removing your registration deletes your account and takes you off the attendee
          list, so the organizers stop planning food and space for you.
        </p>

        {!confirmingRemoval ? (
          <button
            type="button"
            className="btn-danger"
            onClick={() => setConfirmingRemoval(true)}
          >
            Remove my registration
          </button>
        ) : (
          <>
            <p>
              <strong>
                This can't be undone — you'd have to register again and wait for approval.
              </strong>
            </p>
            <div className="form-actions">
              <button
                type="button"
                className="btn-danger"
                disabled={removing}
                onClick={handleRemove}
              >
                {removing ? 'Removing…' : 'Yes, remove me'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={removing}
                onClick={() => setConfirmingRemoval(false)}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
